import MarkerClusterer from "@googlemaps/markerclustererplus";
import {MapSettings, UserSettings} from "@mcleod/core";
import {getSVGUrl} from "@mcleod/images";
import {Panel} from "../panel/Panel";
import {Map, VendorMap} from "./Map";
import {MapPin} from "./MapPin";
import {MapVendor, VendorMapProps} from "./MapProps";
import {DesignableObjectLogManager} from "../../logging/DesignableObjectLogManager";
import { Snackbar } from "../../";

const log = DesignableObjectLogManager.getLogger("components/Map");

interface google {
    maps: typeof window.google.maps,
}

export enum RouteType {
    Google,
    Trimble
}

export class GoogleMap extends Panel implements VendorMap {
    map: google.maps.Map;
    markerClusterer: MarkerClusterer;
    markers: google.maps.Marker[];
    parentMap: Map;
    directionsService: google.maps.DirectionsService;
    directionsRenderer: google.maps.DirectionsRenderer;
    trimblePath: google.maps.Polyline;
    routesAPIPath: google.maps.Polyline;
    advanceMarkers: google.maps.marker.AdvancedMarkerElement[];

    private trafficLayer: google.maps.TrafficLayer;

    private _userSelectionMapType = UserSettings.get()?.google_map_type;
    private _userSelectionMapLayer = UserSettings.get()?.google_map_layer;
    private static scriptLoadPromise: Promise<void>;
    private isMapInitialized: boolean = false;
    private disableDefaultUI: boolean = false;


    constructor(props?: Partial<VendorMapProps>) {
        super({borderRadius: 4, ...props});
        if (props.parentMap == null)
            throw new Error("GoogleMap needs to have its parentMap property set.");
        this.markers = [];
        this.advanceMarkers = [];
        this.parentMap.vendor = MapVendor.GOOGLE;
        this.disableDefaultUI = props?.disableDefaultUI;
    }

    setCenter(latitude: number, longitude: number) {
        this.map.setCenter(new google.maps.LatLng(latitude, -longitude));
    }

    public async ensureLoaded(): Promise<void> {
        try {
            if (window.google == null) {
                if (GoogleMap.scriptLoadPromise == null) {
                    GoogleMap.scriptLoadPromise = this.initHeader();
                }
            }
            await GoogleMap.scriptLoadPromise;
            this.initMap();
        } catch (error) {
            log.error("Error loading GoogleMap", error);
        }
    }


    addPin(pin: MapPin): void {
        if (this.map == null)
            return;
        const image = pin.image == null ? undefined : {
            icon: {
                url: getSVGUrl(pin.image),
                labelOrigin: new google.maps.Point(12, 12)
            }
        };
        const marker = new window.google.maps.Marker({
            ...image,
            position: new window.google.maps.LatLng(pin.latitude, pin.longitude),
            label: {
                text: pin.caption,
                color: pin.captionColor,
                fontSize: "12px"
            },
            title: pin.tooltip
        });

        this.markers.push(marker);
        if (this.parentMap.pinNeedsClickListener(pin))
            marker.addListener("click", (event) => this.parentMap.pinClicked(pin, event.domEvent));
        if (pin.clusterable === true)
            this.markerClusterer.addMarker(marker);
        else
            marker.setMap(this.map);
    }

    focusOnPin(pin: MapPin, mapPinIndex: number) {

    }

    defocusOnPin(pin: MapPin, mapPinIndex: number) {

    }

    removeAllPins() {
        this.markerClusterer?.removeMarkers(this.markers);
        for (const marker of this.markers)
            marker.setMap(null);
        this.markers = [];
    }

    private async initHeader(): Promise<void> {
        const mapSettings = MapSettings.get();
        const url = mapSettings.google_maps_url;
        const apiKey = mapSettings.google_api_key;
        const script = document.createElement("script");
        script.src = url + apiKey;
        window.document.body.appendChild(script);
        return new Promise((resolve, reject) => {
            script.onload = () => {
                log.debug(this, "Map initialized successfully");
                resolve();
            };
            script.onerror = () => {
                log.debug(this, "An error occurred during map initialization");
                reject();
            };
        });
    }

    private getGestureHandling() {
        if (MapSettings.get()?.google_greedy)
            return "greedy";
        else
            return "cooperative";
    }

    private setMapType() {
        const [MapTypeStandard, MapTypeSatellite, MapTypeTerrain, MapTypeStandardPlus] = ['A', 'S', 'T', 'P'];
        const mapTypes = {
            [MapTypeStandard]: 'roadmap',
            [MapTypeSatellite]: 'satellite',
            [MapTypeTerrain]: 'terrain',
            [MapTypeStandardPlus]: 'hybrid'
        };
        const userSelectionMapType = this._userSelectionMapType;
        return mapTypes[userSelectionMapType] || 'roadmap';
    }

    private initMap() {
        if (this.isMapInitialized)
            return;
        this.isMapInitialized = true
        const userSelectionMapType = this.setMapType();

        this.map = new window.google.maps.Map(this._element, {
            disableDefaultUI: this.disableDefaultUI,
            gestureHandling: this.getGestureHandling(),
            center: new window.google.maps.LatLng(39.8283, -98.5795),
            zoom: 5,
            mapTypeId: userSelectionMapType,
            mapId: Math.random().toString()
        });

        this.markerClusterer = new MarkerClusterer(this.map, [], {
            styles: [
                {
                    textColor: "white",
                    anchorText: [18, 0],
                    fontWeight: "600",
                    url: getSVGUrl("map-pin-cluster"),
                    height: 48,
                    width: 48
                }
            ],

            maxZoom: 12,
            gridSize: 20
        });
        this.directionsService = new google.maps.DirectionsService();
        this.directionsRenderer = new google.maps.DirectionsRenderer();

        this.initTrafficLayer();
        this.applyUserSettingForTrafficLayer();
        if(!this.disableDefaultUI) {
            this.setupTrafficToggleButton();
        }
    }

    private initTrafficLayer(): void {
        this.trafficLayer = new google.maps.TrafficLayer();
    }

    private applyUserSettingForTrafficLayer(): void {
        if ("Y" === this._userSelectionMapLayer) {
            this.trafficLayer.setMap(this.map);
        } else {
            this.trafficLayer.setMap(null);
        }
    }

    private setupTrafficToggleButton(): void {
        const toggleBtn = document.createElement('button');
        toggleBtn.style.backgroundColor = '#fff';
        toggleBtn.style.border = '2px solid #fff';
        toggleBtn.style.borderRadius = '3px';
        toggleBtn.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        toggleBtn.style.color = 'rgb(25,25,25)';
        toggleBtn.style.cursor = 'pointer';
        toggleBtn.style.fontFamily = 'Roboto,Arial,sans-serif';
        toggleBtn.style.fontSize = '16px';
        toggleBtn.style.lineHeight = '38px';
        toggleBtn.style.margin = '20px 0 22px';
        toggleBtn.style.padding = '0 5px';
        toggleBtn.style.textAlign = 'center';
        toggleBtn.textContent = 'Toggle Traffic';
        toggleBtn.onclick = () => {
            if (this.trafficLayer.getMap()) {
                this.trafficLayer.setMap(null);
            } else {
                this.trafficLayer.setMap(this.map);
            }
        };
        this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(toggleBtn);
    }

    fitPins() {
        if (this.map == null)
            return;
        if (this.markers.length === 1)
            this.map.setCenter(this.markers[0].getPosition());
        else if (this.markers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            for (const marker of this.markers)
                bounds.extend(marker.getPosition());
            this.map.fitBounds(bounds);
        }
        if (this.map.getZoom() > 10)
            this.map.setZoom(10);
    }

    setZoom(level: number) {
        this.map.setZoom(level);
    }

    setStopZoomLevel: (isTrafficEnabled: string, routeData: any[]) => void;

    createRoute(routeData: any[], routeType: RouteType, hideMarkers?: boolean | false) {
        this.clearRoute();
        const useDirectionsAPI = MapSettings.get().use_legacy_google_api;
        if (routeType == RouteType.Google) {
            const data: google.maps.LatLngLiteral[] = this.formatGoogleRouteData(routeData);

            if (useDirectionsAPI) {
                this.setupDirectionsAPIRequest(hideMarkers, data);
            } else {
                this.setupRoutesAPIRequest(data);
                if (!hideMarkers) {
                    this.addMarkers(data);
                }
            }
        }
        if (routeType == RouteType.Trimble) {
            if (this.trimblePath) {
                this.trimblePath.setMap(null);
            }
            this.trimblePath = new google.maps.Polyline({
                path: routeData,
                geodesic: true,
                strokeColor: "#FF0000",
                strokeOpacity: 1.0,
                strokeWeight: 4,
                zIndex: useDirectionsAPI ? null : 1
            });
            this.trimblePath.setMap(this.map);
        }
    }

    private setupDirectionsAPIRequest(hideMarkers: boolean, data: google.maps.LatLngLiteral[]) {
        let waypoints: google.maps.DirectionsWaypoint[];
        if (hideMarkers) {
            waypoints = data.slice(1, -1).map(point => ({
                location: point,
                stopover: false // Prevent waypoints from being displayed as markers
            })) as google.maps.DirectionsWaypoint[];
        } else {
            waypoints = data.slice(1, -1) as google.maps.DirectionsWaypoint[];
        }
        log.debug(this, "Google Directions API Request");
        this.directionsService
            .route({
                origin: data[0]["location"],
                destination: data[data.length - 1]["location"],
                waypoints: waypoints,
                travelMode: google.maps.TravelMode.DRIVING
            })
            .then((response) => {
                log.debug(this, "Google Directions API Response", response);
                this.directionsRenderer.setDirections(response);
                if (hideMarkers) {
                    this.directionsRenderer.setOptions({
                        suppressMarkers: true // Prevent origin and destination markers from displaying
                    });
                }
                this.directionsRenderer.setMap(this.map);
            });
    }

    private async setupRoutesAPIRequest(data: google.maps.LatLngLiteral[]) {
        const endpoint = "https://routes.googleapis.com/directions/v2:computeRoutes";
        const headers = {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": MapSettings.get().google_api_key,
            "X-Goog-FieldMask": "routes.polyline.encodedPolyline"
        }

        const origin = data[0];
        const destination = data[data.length-1];
        const stops = [];

        // Add intermediate stops if they are present
        if (data.length > 2) {
            // Do not include the Origin and Destination
            const intermediateStops = data.slice(1, -1);
            intermediateStops.forEach(stop => {
                stops.push(`{ "latLng": { "latitude": ${stop['location'].lat}, "longitude": ${Math.abs(stop['location'].lng) * -1} } }`);
            });
            log.debug(this, "Intermediate Stops: " + stops);
        }

        const request = {
            "origin" : {
                "location" : {
                    latLng: {
                        latitude: origin["location"].lat,
                        longitude: origin["location"].lng
                    }
                }
            },
            destination: {
                location: {
                    latLng: {
                        latitude: destination["location"].lat,
                        longitude: destination["location"].lng
                    }
                }
            },
            intermediates: stops.map(stop => ({
                location: JSON.parse(stop),
            })),
            travelMode: "DRIVE",
            routingPreference: "TRAFFIC_UNAWARE",
            computeAlternativeRoutes: false,
            routeModifiers: {
                avoidTolls: false,
                avoidHighways: false,
                avoidFerries: false
            },
            languageCode: "en-US",
            units: "IMPERIAL"
        }

        log.debug(this, "Google Routes API Request", endpoint, JSON.stringify(request));
        const resp = await fetch(endpoint, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(request)
        })

        const obj = await resp.json();
        log.debug(this, "Google Routes API Response", endpoint, obj);

        const msg = "Google Routes could not find a route.";
        if (!resp.ok) {
            log.debug(this, "Google Routes API request failed.  " + obj.error.message + "\n\nCode:  " + obj.error.code + "\nStatus: " + obj.error.status);
            Snackbar.showWarningSnackbar(msg);
            return;
        }

        if (obj.routes) {
            const encodedPolyline = obj.routes[0].polyline.encodedPolyline;
            const decodedPolyline = this.decodePolyline(encodedPolyline);
            this.addRouteLineAndFitMapBounds(decodedPolyline);
        } else {
            log.debug(this, "Google Routes API Response did not return a route.");
            Snackbar.showWarningSnackbar(msg);
        }
    }

    private addRouteLineAndFitMapBounds(decodedPolyline: google.maps.LatLng[]) {
        this.routesAPIPath = new google.maps.Polyline({
            path: decodedPolyline,
            geodesic: true,
            strokeColor: "#0000FF",
            strokeOpacity: 0.5,
            strokeWeight: 8,
        });

        this.routesAPIPath.setMap(this.map);
        this.addMapBounds(decodedPolyline);
    }

    private decodePolyline(polyline: any) {
        return google.maps.geometry.encoding.decodePath(polyline);
    }

    async addMarkers(data: google.maps.LatLngLiteral[]) {
        const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        // Create custom glyph text starting at upper case 'A'
        let letter = 65; // ASCII 'A'
        data.forEach(stop => {
            const pinTextGlyph = new PinElement({
                glyph: String.fromCharCode(letter++),
                glyphColor: 'white',
            });

            const marker = new AdvancedMarkerElement({
                map: this.map,
                position: { lat: Number(stop["location"].lat), lng: Number(Math.abs(stop["location"].lng) * -1) },
                content: pinTextGlyph.element
           });

           this.advanceMarkers.push(marker);
        });
    }

    addMapBounds(decodedPoly: google.maps.LatLng[]) {
        const bounds = new google.maps.LatLngBounds();
        // Use array of coords
        for (let i = 0; i < decodedPoly.length; i++) {
            bounds.extend(decodedPoly[i]);
        }
        this.map.fitBounds(bounds);
    }

    clearRoute() {
        this.directionsRenderer.setMap(null);
        if (this.trimblePath) {
            this.trimblePath.setMap(null);
        }
        if (this.routesAPIPath) {
            this.routesAPIPath.setMap(null);
        }
        this.advanceMarkers.forEach(marker => {
            marker.map = null;
        })
    }

    private formatGoogleRouteData(routeData: any[]): google.maps.LatLngLiteral[] {
        const coordinatesList = [];
        routeData.forEach(stop => {
            const lat = Number(stop.latitude);
            const lng = Number(Math.abs(stop.longitude) * -1);
            coordinatesList.push({"location": {lat, lng}});
        });
        return coordinatesList;
    }

}
